import { useCallback, useEffect, useMemo, useRef } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import type { StepId } from "@autopic/shared";
import { ProgressIndicator } from "../components/ProgressIndicator";
import { SessionTimer } from "../components/SessionTimer";
import { HeaderCard } from "../components/HeaderCard";
import { Button } from "../components/Button";
import { api, ApiError } from "../api/client";
import { initAnalytics, track } from "../analytics/analytics";
import { isEmbedded, sendReady, startResizeReporter } from "../iframe/bridge";
import { currentStepId, useSession } from "../store/sessionStore";
import { useLeadAutosave } from "../store/useLeadAutosave";
import { RegistrationStep } from "./steps/RegistrationStep";
import { VehicleStep } from "./steps/VehicleStep";
import { LocationStep } from "./steps/LocationStep";
import { ContactStep } from "./steps/ContactStep";
import { MotStep } from "./steps/MotStep";
import { ServicesStep } from "./steps/ServicesStep";
import { DateTimeStep } from "./steps/DateTimeStep";
import { DetailsStep } from "./steps/DetailsStep";
import { SummaryStep } from "./steps/SummaryStep";
import { ConfirmationStep } from "./steps/ConfirmationStep";
import type { StepProps } from "./stepTypes";
import styles from "./BookingFlow.module.css";

interface BookingFlowProps {
  garageId: string;
}

const STEP_KICKERS: Record<StepId, string> = {
  registration: "Registration",
  vehicle: "Vehicle",
  location: "Location",
  contact: "Contact",
  mot: "MOT",
  services: "Services",
  datetime: "Date & time",
  details: "Details",
  summary: "Summary",
  confirmation: "Done",
};

export function BookingFlow({ garageId }: BookingFlowProps) {
  const [params] = useSearchParams();
  const singleGaragePreview =
    garageId === "demo-sandbox" && params.get("preview") === "single-garage";
  const init = useSession((s) => s.init);
  const config = useSession((s) => s.config);
  const configStatus = useSession((s) => s.configStatus);
  const configError = useSession((s) => s.configError);
  const setConfig = useSession((s) => s.setConfig);
  const setConfigStatus = useSession((s) => s.setConfigStatus);

  const measureRef = useRef<HTMLDivElement>(null);

  // ---- Load config ----
  useEffect(() => {
    init(garageId, singleGaragePreview);
  }, [garageId, singleGaragePreview, init]);

  useEffect(() => {
    const st = useSession.getState();
    if (st.garageId !== garageId) return;
    if (st.config || st.configStatus === "loading") return;
    const ctrl = new AbortController();
    setConfigStatus("loading");
    api
      .getConfig(garageId, ctrl.signal)
      .then((cfg) => {
        setConfig(cfg);
        initAnalytics({
          garageId: cfg.id,
          sessionId: st.sessionId,
          sandbox: cfg.sandbox,
        });
        track("widget_loaded", { embedded: isEmbedded(), slug: cfg.slug });
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setConfigStatus(
          "error",
          err instanceof ApiError
            ? err.message
            : "We couldn't load this booking widget.",
        );
      });
    return () => ctrl.abort();
  }, [garageId, singleGaragePreview, setConfig, setConfigStatus]);

  // ---- Iframe handshake + resize reporting ----
  useEffect(() => {
    sendReady();
  }, []);

  useEffect(() => {
    if (!measureRef.current) return;
    return startResizeReporter(measureRef.current);
  }, [config, configStatus]);

  // ---- Abandonment beacon ----
  useEffect(() => {
    const onHide = () => {
      const st = useSession.getState();
      const step = currentStepId(st);
      if (st.bookingReference || !st.config) return;
      if (step === "confirmation") return;
      track("booking_abandoned", { lastStep: step });
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, []);

  if (configStatus === "error") {
    return (
      <div className={styles.shell} ref={measureRef}>
        <div className={styles.inner}>
          <div className={styles.stage}>
            <div className={styles.errorState}>
              <p className={styles.errorTitle}>
                We couldn&rsquo;t load this widget
              </p>
              <p>{configError ?? "Please try again shortly."}</p>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!config || configStatus !== "ready") {
    return (
      <div className={styles.shell} ref={measureRef}>
        <div className={styles.inner}>
          <div className={styles.stage}>
            <div className={styles.loading}>
              <Loader2 size={28} className={styles.loadingSpinner} />
              <p>Loading your booking…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <FlowBody garageId={garageId} measureRef={measureRef} />;
}

function FlowBody({
  measureRef,
}: {
  garageId: string;
  measureRef: React.RefObject<HTMLDivElement>;
}) {
  const config = useSession((s) => s.config)!;
  const stepIndex = useSession((s) => s.stepIndex);
  const previousStepIndex = useRef(stepIndex);
  const next = useSession((s) => s.next);
  const back = useSession((s) => s.back);
  const goToStep = useSession((s) => s.goToStep);
  const bookingReference = useSession((s) => s.bookingReference);

  const vehicle = useSession((s) => s.vehicle);
  const vehicleReg = useSession((s) => s.vehicleRegistration);
  const branchId = useSession((s) => s.branchId);
  const branches = useSession((s) => s.branches);

  const saveLead = useLeadAutosave();

  const steps = config.flow.steps;
  const stepId = steps[Math.min(stepIndex, steps.length - 1)]!;

  // Visible (numbered) steps exclude confirmation from the X/Y count.
  const visibleSteps = useMemo(
    () => steps.filter((s) => s !== "confirmation"),
    [steps],
  );
  const isConfirmation = stepId === "confirmation";
  const currentVisibleNo = Math.min(stepIndex + 1, visibleSteps.length);

  const goTo = useCallback(
    (target: StepId) => {
      const idx = steps.indexOf(target);
      if (idx >= 0) goToStep(idx);
    },
    [steps, goToStep],
  );

  const stepProps: StepProps = { onNext: next, goTo, saveLead };

  // Keep automatic embeds from moving the host page on their first render.
  // Scroll only when the visitor moves between steps in a tall iframe/page.
  useEffect(() => {
    if (previousStepIndex.current === stepIndex) return;
    previousStepIndex.current = stepIndex;
    measureRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [stepIndex, measureRef]);

  const stepNode = (() => {
    switch (stepId) {
      case "registration":
        return <RegistrationStep {...stepProps} />;
      case "vehicle":
        return <VehicleStep {...stepProps} />;
      case "location":
        return <LocationStep {...stepProps} />;
      case "contact":
        return <ContactStep {...stepProps} />;
      case "mot":
        return <MotStep {...stepProps} />;
      case "services":
        return <ServicesStep {...stepProps} />;
      case "datetime":
        return <DateTimeStep {...stepProps} />;
      case "details":
        return <DetailsStep {...stepProps} />;
      case "summary":
        return <SummaryStep {...stepProps} />;
      case "confirmation":
        return <ConfirmationStep />;
      default:
        return null;
    }
  })();

  // Header card context — show once the vehicle is known, hide on the first
  // couple of steps and on confirmation.
  const vehicleLabel =
    vehicle && "make" in vehicle
      ? `${vehicle.make} ${(vehicle as { model: string }).model}`
      : null;
  const availableBranches = branches.length ? branches : config.branches;
  const selectedBranch =
    availableBranches.find((b) => b.id === branchId) ?? null;
  const canChangeLocation =
    availableBranches.length > 1 && steps.includes("location");
  const showHeaderCard =
    !isConfirmation && stepIndex > 1 && (!!vehicleLabel || !!selectedBranch);

  const canGoBack = !isConfirmation && stepIndex > 0;

  return (
    <div className={styles.shell} ref={measureRef}>
      <div className={styles.inner}>
        {/* Top chrome */}
        {!isConfirmation && (
          <div className={styles.chrome}>
            {canGoBack ? (
              <button type="button" className={styles.backBtn} onClick={back}>
                <ArrowLeft size={16} /> Back
              </button>
            ) : (
              <span aria-hidden="true" />
            )}
            <div className={styles.chromeRight}>
              {config.sessionTimer.enabled && (
                <SessionTimer
                  config={config.sessionTimer}
                  active={!bookingReference}
                />
              )}
              <ProgressIndicator
                current={currentVisibleNo}
                total={visibleSteps.length}
                label={`Step ${currentVisibleNo} of ${visibleSteps.length}: ${STEP_KICKERS[stepId]}`}
              />
            </div>
          </div>
        )}

        {/* Persistent context */}
        {showHeaderCard && (
          <HeaderCard
            vehicleLabel={vehicleLabel}
            registration={vehicleReg}
            branchLabel={selectedBranch?.name ?? null}
            onChangeVehicle={() => goTo("registration")}
            onChangeBranch={
              selectedBranch && canChangeLocation
                ? () => goTo("location")
                : undefined
            }
          />
        )}

        {/* Step stage */}
        <div className={styles.stage}>
          <div key={stepId} className={styles.stepAnim}>
            {stepNode}
          </div>
        </div>
      </div>
    </div>
  );
}
